import { defineStore } from 'pinia'
import useGet from '../core/composables/api/useGet'
import type { Organization } from '../core/models/organization'
import { useLocalStorage } from '@vueuse/core'
import { ApiRequestStatus } from '../core/models/api/api-request-status'
import { InitializationState, useInitializationStore } from './initialization.store'

export const useOrganizationStore = defineStore('organization', {
  state: () => ({
    organizations: [] as Organization[],
    currentOrganizationId: useLocalStorage('current-organization-unit-id', ''),
    organizationState: OrganizationState.Uninitialized,
  }),
  getters: {
    currentOrganization(): Organization {
      const currentOrg = this.organizations.find(
        (organization) => organization.id === this.currentOrganizationId,
      )

      if (!currentOrg) {
        throw new Error('Current Org not Initialized')
      }

      return currentOrg
    },
  },
  actions: {
    async initializeOrganizations() {
      const { data, load, status, error } = useGet<Organization[]>('organizations', {
        omitOrganization: true,
      })

      await load()

      if (status.value === ApiRequestStatus.Unavailable) {
        this.organizationState = OrganizationState.Unavailable
        return
      }

      if (status.value !== ApiRequestStatus.Success || !data.value.length) {
        this.organizationState = OrganizationState.Error
        console.error(error)
        return
      }

      this.organizations = data.value

      if (this.organizations.length == 0) {
        this.organizationState = OrganizationState.NoOrganization
        return
      }

      let organizationId: string | undefined = undefined

      if (this.isValidOrganization(this.currentOrganizationId)) {
        organizationId = this.currentOrganizationId
      } else if (this.isValidOrganization(this.organizations[0].id)) {
        organizationId = this.organizations[0].id
      }

      if (organizationId != undefined) {
        await this.switchOrganization(organizationId)
        this.organizationState = this.currentOrganization.pending
          ? OrganizationState.PendingAcceptance
          : OrganizationState.Initialized
      } else {
        this.organizationState = OrganizationState.NoOrganization
      }
    },
    async switchOrganization(newOrganizationId: string) {
      const { setInitializationState } = useInitializationStore()

      this.currentOrganizationId = newOrganizationId
      this.organizationState = OrganizationState.Uninitialized
      setInitializationState(InitializationState.Uninitialized)
    },
    isValidOrganization(organizationId: string): boolean {
      return this.organizations.some((organization) => organization.id === organizationId)
    },
  },
})

export enum OrganizationState {
  Uninitialized = 'uninitialized',
  NoOrganization = 'noOrganization',
  PendingAcceptance = 'pendingAcceptance',
  Initialized = 'initialized',
  Error = 'error',
  Unavailable = 'unavailable',
}
